/**
 * Unified search — the one model behind every search entry point: the topbar pill, the home hero
 * editor, the space-type chips above the results of the city pages and of /s, and the zero-result
 * ladder. Spec: docs/unified-search/spec.md.
 *
 * Three questions: WHERE (one of our city pages, a place with bounds, the visible map area, or
 * nothing = all of Switzerland), the SPACE TYPE (one of the three groups of categoryGroups.js) and,
 * since 2026-10-30 on the owner's decision (spec §2 had left it in the panel), WHEN — a date range
 * that is the /s `dates` filter. Length, persons and price stay in the /s filter panel.
 *
 * The URL is the only source of the applied state. These are pure functions: the server render
 * and the first client render read the same URL and get the same answer.
 *
 * Gated by REACT_APP_UNIFIED_SEARCH — a build-time constant in the web and the server bundle, like
 * REACT_APP_AI_CONCIERGE, so SSR and hydration always agree.
 */

import { stringify, UI_ONLY_PARAMS } from './urlHelpers';
import { ATTRIBUTION_PARAMS } from './attribution';
import { convertUnitToSubUnit, unitDivisor } from './currency';
import {
  CITY_LANDING_SLUGS,
  getCityLandingConfig,
  cityTypeSlugToGroup,
  cityTypeGroupToSlug,
  isIndexableCityType,
  getBoundsForSdk,
} from './cityLandingPageConfig';

/** @returns {boolean} whether the unified search replaces the old entry points */
export const isUnifiedSearchEnabled = () => process.env.REACT_APP_UNIFIED_SEARCH === 'true';

// ================ Vocabulary ================ //

/** The public category param every chip, pill and shortcut writes (constructQueryParamName). */
export const CATEGORY_PARAM = 'pub_categoryLevel1';
/** The city pages' deep-link param (`?typ=meetingraum`), also what the Google Ads final URLs carry. */
export const CITY_TYPE_PARAM = 'typ';
/**
 * City-page refinements (client-side over the page's one query): `sort` with the same values as
 * /s (`-price` = lowest price first, `price` = highest first — configSearch.js), and
 * `verifiziert=ja` for providers with the verified badge (profile.metadata.stripeVerified, which
 * the Marketplace API cannot filter by — so /s has no such filter).
 */
export const CITY_SORT_PARAM = 'sort';
export const CITY_SORT_VALUES = ['-price', 'price'];
export const CITY_VERIFIED_PARAM = 'verifiziert';
export const CITY_VERIFIED_VALUE = 'ja';

/** The city refinements present in a query string, validated (unknown values dropped). */
export const cityRefinementsFromSearch = search => {
  const params = new URLSearchParams(typeof search === 'string' ? search : '');
  const sort = params.get(CITY_SORT_PARAM);
  return {
    ...(CITY_SORT_VALUES.includes(sort) ? { [CITY_SORT_PARAM]: sort } : {}),
    ...(params.get(CITY_VERIFIED_PARAM) === CITY_VERIFIED_VALUE
      ? { [CITY_VERIFIED_PARAM]: CITY_VERIFIED_VALUE }
      : {}),
  };
};

/**
 * The WHEN of the bar is the /s `dates` filter (`YYYY-MM-DD,YYYY-MM-DD`, SearchPage.duck turns it
 * into an availability query). City pages have no calendar query, so a submit with dates always
 * lands on /s. The state carries the two days as ISO strings — serialisable, same on the server
 * and in the first client render.
 */
export const DATES_PARAM = 'dates';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day in ISO shape — "2026-13-45" has the shape but no such day exists. */
const isIsoDay = value => {
  if (!ISO_DAY.test(`${value}`)) {
    return false;
  }
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

/**
 * `dates` param → `{ start, end }` (ISO days), or null when absent, malformed or not real days.
 *
 * @param {string|null|undefined} value e.g. "2026-11-03,2026-11-05"
 * @returns {{ start: string, end: string }|null}
 */
export const parseDatesParam = value => {
  const [start, end] = `${value ?? ''}`.split(',');
  return isIsoDay(start) && isIsoDay(end) && start <= end ? { start, end } : null;
};

/**
 * `{ start, end }` → the `dates` param, or null when incomplete.
 *
 * @param {{ start: string, end: string }|null|undefined} dates
 * @returns {string|null}
 */
export const datesParamOf = dates =>
  dates && isIsoDay(dates.start) && isIsoDay(dates.end) && dates.start <= dates.end
    ? `${dates.start},${dates.end}`
    : null;

export const WHERE_CITY = 'city';
export const WHERE_PLACE = 'place';
export const WHERE_MAP = 'map';

export const PAGE_SEARCH = 'search';
export const PAGE_CITY = 'city';
export const PAGE_OTHER = 'other';

const SEARCH_ROUTES = ['SearchPage', 'SearchPageWithListingType'];
const CITY_ROUTES = ['CityLandingPage', 'CityLandingTypePage'];

const CITY_PATH_PREFIX = '/coworking';
const SEARCH_PATH = '/s';

/**
 * Params of /s that are the WHERE or the SPACE TYPE (owned by the unified search), or that are
 * never a filter (paging, map state, the overlay params). Everything else on /s is a panel filter.
 */
const NON_FILTER_PARAMS = [
  'address',
  'bounds',
  'origin',
  'mapSearch',
  'page',
  CATEGORY_PARAM,
  ...UI_ONLY_PARAMS,
];
/**
 * Ad click ids and campaign tags ride on the landing URL (Google auto-tagging adds `gclid` to every
 * ad click). They are not filters: counted as such they kept an Ads visitor off the city pages and
 * turned a zero result into "Keine Angebote mit diesen Filtern" with no way out.
 *
 * ATTRIBUTION_PARAMS is read at call time, not spread at module load: tests that mock
 * util/attribution.js without it (factory mocks) otherwise broke every importer of the store.
 */
const OTHER_TRACKING_PARAMS = [
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'gad_source',
  'gad_campaignid',
  '_gl',
];
const isTrackingParam = key =>
  key.startsWith('utm_') ||
  OTHER_TRACKING_PARAMS.includes(key) ||
  (ATTRIBUTION_PARAMS || []).includes(key);

/** Panel params that do not stop a submit from going to a city page: sorting is not a filter. */
const NON_BLOCKING_PARAMS = ['sort'];

/**
 * Every spelling of our cities a person or the geocoder may produce. The first segment of a
 * Mapbox place name ("Zürich, Zürich, Schweiz" → "Zürich") is matched against these after
 * normalisation, so a Mapbox "Genève" lands on the same city page as our own "Genf".
 */
const CITY_NAME_ALIASES = {
  zurich: ['zurich', 'zuerich'],
  bern: ['bern', 'berne'],
  basel: ['basel', 'bale'],
  geneva: ['geneva', 'genf', 'geneve', 'ginevra'],
  lausanne: ['lausanne'],
  lucerne: ['luzern', 'lucerne', 'lucerna'],
  'st-gallen': ['st gallen', 'st. gallen', 'sankt gallen', 'saint-gall', 'san gallo'],
  winterthur: ['winterthur'],
  zug: ['zug', 'zoug'],
};

const normaliseName = name =>
  `${name || ''}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const ALIAS_TO_SLUG = Object.entries(CITY_NAME_ALIASES).reduce((map, [slug, aliases]) => {
  aliases.forEach(alias => map.set(normaliseName(alias), slug));
  return map;
}, new Map());

/**
 * Which of our city pages a place name means, if any.
 *
 * @param {string} placeName e.g. "Zürich" or the Mapbox "Zürich, Zürich, Schweiz"
 * @returns {string|null} city slug
 */
export const citySlugForPlaceName = placeName => {
  const first = `${placeName || ''}`.split(',')[0];
  const slug = ALIAS_TO_SLUG.get(normaliseName(first));
  return slug && CITY_LANDING_SLUGS.includes(slug) ? slug : null;
};

/**
 * The `address` a city writes into /s when no translated label is at hand (the WHERE read from a
 * city page carries none): without it /s read the city box as "Kartenausschnitt". Same words as
 * the German `CityLandingPage.<slug>.placeName` keys; `citySlugForPlaceName` maps them back.
 */
const CITY_ADDRESS = {
  zurich: 'Zürich',
  bern: 'Bern',
  basel: 'Basel',
  geneva: 'Genf',
  lausanne: 'Lausanne',
  lucerne: 'Luzern',
  'st-gallen': 'St. Gallen',
  winterthur: 'Winterthur',
  zug: 'Zug',
};

/** Translation id of a city's display name (repo keys, not hosted). */
export const cityNameMessageId = slug => `CityLandingPage.${slug}.placeName`;

/**
 * The WHERE of a city page.
 *
 * @param {string} slug city slug
 * @param {string} [label] display name, translated by the caller
 * @returns {Object|null}
 */
export const cityWhere = (slug, label) => {
  const city = getCityLandingConfig(slug);
  return city ? { kind: WHERE_CITY, citySlug: slug, boundsStr: city.boundsStr, label } : null;
};

// ================ Reading ================ //

const rawParams = search => {
  try {
    return new URLSearchParams(typeof search === 'string' ? search : '');
  } catch (e) {
    return new URLSearchParams('');
  }
};

/** `?typ=` → group key; untrusted input, own keys only, malformed escapes read as nothing. */
const groupForTypParam = value => {
  const word = `${value || ''}`.toLowerCase().replace('ü', 'ue');
  return cityTypeSlugToGroup(word);
};

/**
 * Which group a category param value selects. Exactly one group whose ids contain every value —
 * the same rule as `selectedSpaceType`, so a single raw id (footer, concierge) also reads as its
 * group. Anything else (a lounge search, ids of two groups) is "no space type".
 */
export const spaceTypeForCategoryParam = (value, groups = []) => {
  const ids = `${value ?? ''}`
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);
  if (ids.length === 0) {
    return null;
  }
  const group = groups.find(g => ids.every(id => g.ids.includes(id)));
  return group ? group.key : null;
};

/**
 * The applied search, read from the route and the URL.
 *
 * @param {Object} p
 * @param {string} [p.routeName] matched route name
 * @param {Object} [p.pathParams] matched route params
 * @param {string} [p.search] location.search
 * @param {Array} [p.groups] resolved space-type groups (resolveGroups)
 * @returns {{ page: string, where: Object|null, spaceType: string|null, dates: Object|null,
 *   filters: Object, listingTypePath: string|null, isSpaceTypeExact: boolean }}
 *   `filters` = the /s panel params as raw strings (the `dates` param stays among them too, so the
 *   panel, the zero-result ladder and the city rule keep reading it as the filter it is);
 *   `dates` = the same param parsed for the bar; `isSpaceTypeExact` is false when the URL
 *   carries a subset of a group (a single raw id) — the chips then show the group as selected,
 *   and selecting it again writes the whole group.
 */
export const readSearchState = ({ routeName, pathParams = {}, search = '', groups = [] } = {}) => {
  const params = rawParams(search);

  if (CITY_ROUTES.includes(routeName)) {
    const citySlug = pathParams.citySlug;
    const where = cityWhere(citySlug);
    const fromPath = pathParams.typSlug ? cityTypeSlugToGroup(pathParams.typSlug) : null;
    const spaceType = fromPath || groupForTypParam(params.get(CITY_TYPE_PARAM));
    return {
      page: PAGE_CITY,
      where,
      spaceType: spaceType || null,
      dates: null,
      filters: cityRefinementsFromSearch(search),
      listingTypePath: null,
      isSpaceTypeExact: true,
    };
  }

  if (SEARCH_ROUTES.includes(routeName)) {
    const address = params.get('address');
    const boundsStr = params.get('bounds');
    // A map move keeps the old `address` but writes the visible area into `bounds` with
    // `mapSearch=true` (SearchPageWithMap.onMapMoveEnd): the results are the map area then, not
    // the named place — reading it as "Zürich" made the pill lie and sent the next submit to the
    // Zürich page instead of the area on screen.
    const isMapArea = params.get('mapSearch') === 'true';
    const where =
      address && boundsStr && !isMapArea
        ? {
            kind: WHERE_PLACE,
            address,
            boundsStr,
            label: address.split(',')[0],
            citySlug: citySlugForPlaceName(address),
          }
        : boundsStr && getBoundsForSdk(boundsStr)
        ? { kind: WHERE_MAP, boundsStr }
        : null;

    const categoryValue = params.get(CATEGORY_PARAM);
    const spaceType = spaceTypeForCategoryParam(categoryValue, groups);
    const group = groups.find(g => g.key === spaceType);
    const isSpaceTypeExact =
      !group ||
      `${categoryValue}`
        .split(',')
        .filter(Boolean)
        .sort()
        .join(',') === [...group.ids].sort().join(',');

    const filters = {};
    params.forEach((value, key) => {
      if (!NON_FILTER_PARAMS.includes(key) && !isTrackingParam(key)) {
        filters[key] = value;
      }
    });

    return {
      page: PAGE_SEARCH,
      where,
      spaceType,
      dates: parseDatesParam(params.get(DATES_PARAM)),
      filters,
      listingTypePath: routeName === 'SearchPageWithListingType' ? pathParams.listingType : null,
      isSpaceTypeExact,
    };
  }

  return {
    page: PAGE_OTHER,
    where: null,
    spaceType: null,
    dates: null,
    filters: {},
    listingTypePath: null,
    isSpaceTypeExact: true,
  };
};

/** Panel filters that keep a submit on /s (sorting alone does not). */
export const blockingFilterKeys = (state = {}) => [
  ...Object.keys(state.filters || {}).filter(key => !NON_BLOCKING_PARAMS.includes(key)),
  ...(state.listingTypePath ? ['listingType'] : []),
];

// ================ Writing ================ //

/** The city slug a WHERE stands for: a city page, or a place whose name is one of our cities. */
export const citySlugOfWhere = where =>
  where?.kind === WHERE_CITY
    ? where.citySlug
    : where?.kind === WHERE_PLACE
    ? where.citySlug || citySlugForPlaceName(where.address)
    : null;

const cityPath = (slug, spaceType, refinements = {}) => {
  const typSlug = spaceType ? cityTypeGroupToSlug(spaceType) : null;
  const base = `${CITY_PATH_PREFIX}/${encodeURIComponent(slug)}`;
  const onPath = typSlug && isIndexableCityType(slug, typSlug);
  const query = stringify({
    ...(typSlug && !onPath ? { [CITY_TYPE_PARAM]: typSlug } : {}),
    ...refinements,
  });
  const path = onPath ? `${base}/${typSlug}` : base;
  return query ? `${path}?${query}` : path;
};

/**
 * The URL of the current city page with other refinements (sort, verified only) — the type and
 * the city stay.
 *
 * @param {Object} current readSearchState of a city page
 * @param {Object} refinements `{ sort?, verifiziert? }`; null/undefined values are dropped
 * @returns {string|null}
 */
export const cityRefinementHref = (current = {}, refinements = {}) => {
  if (current.page !== PAGE_CITY || !current.where?.citySlug) {
    return null;
  }
  const next = { ...current.filters, ...refinements };
  const clean = Object.keys(next).reduce((out, key) => {
    if (next[key] != null && next[key] !== '') {
      out[key] = next[key];
    }
    return out;
  }, {});
  return cityPath(current.where.citySlug, current.spaceType, clean);
};

/**
 * Where an editor submit goes (spec §4.3).
 *
 * 1. WHERE is one of our cities, no dates are chosen and no panel filter is active (and no extra
 *    params) → the city page, the space type as `/:typ` where that page is indexable, else
 *    `?typ=` — the format of the Google Ads final URLs.
 * 2. Otherwise /s (or /s/:listingType when already there) with address, bounds, the full group
 *    id list and the dates; panel filters are carried only when the visitor is already on /s.
 * 3. No WHERE → /s without bounds (all of Switzerland).
 *
 * The draft owns the dates: `{ start, end }` writes them, `null` drops the ones the page has, and
 * `undefined` (a caller that only moves WHERE or the type, e.g. the space-type options) keeps
 * what is in the URL of /s.
 *
 * @param {Object} p
 * @param {Object} p.draft `{ where, spaceType, dates }` — where as returned by readSearchState or
 *   built by the editor (`{ kind: 'place', address, boundsStr }`, `cityWhere(slug, label)`)
 * @param {Object} [p.current] readSearchState of the page the submit happens on
 * @param {Array} [p.groups] resolved space-type groups
 * @param {Object} [p.extraParams] params to add on /s (keywords, concierge output); forces /s
 * @returns {string} path + query
 */
export const buildSearchTarget = ({ draft = {}, current = {}, groups = [], extraParams = {} }) => {
  const { where, spaceType } = draft;
  const extras = Object.keys(extraParams).reduce((picked, key) => {
    const value = extraParams[key];
    if (value !== null && value !== undefined && value !== '') {
      picked[key] = value;
    }
    return picked;
  }, {});

  const onSearchPage = current.page === PAGE_SEARCH;
  const { [DATES_PARAM]: carriedDates, ...carried } = onSearchPage ? { ...current.filters } : {};
  const dates = draft.dates === undefined ? parseDatesParam(carriedDates) : draft.dates;
  const datesParam = datesParamOf(dates);
  const blocking = onSearchPage ? blockingFilterKeys({ ...current, filters: carried }) : [];

  const citySlug = citySlugOfWhere(where);
  if (citySlug && !datesParam && blocking.length === 0 && Object.keys(extras).length === 0) {
    return cityPath(citySlug, spaceType);
  }

  const group = groups.find(g => g.key === spaceType);
  const whereParams =
    where?.kind === WHERE_CITY
      ? { address: where.label || CITY_ADDRESS[where.citySlug], bounds: where.boundsStr }
      : where?.kind === WHERE_PLACE
      ? { address: where.address, bounds: where.boundsStr }
      : where?.kind === WHERE_MAP
      ? { bounds: where.boundsStr, mapSearch: true }
      : {};

  const query = stringify({
    ...carried,
    ...whereParams,
    [CATEGORY_PARAM]: group ? group.ids.join(',') : undefined,
    [DATES_PARAM]: datesParam || undefined,
    ...extras,
  });

  const path =
    onSearchPage && current.listingTypePath
      ? `${SEARCH_PATH}/${encodeURIComponent(current.listingTypePath)}`
      : SEARCH_PATH;
  return query ? `${path}?${query}` : path;
};

/**
 * The URL of the current page with another space type — what a chip above the results writes.
 * City pages keep their city (path type where indexable, else `?typ=`); /s keeps every other
 * param and drops the page number.
 *
 * @param {Object} p
 * @param {Object} p.current readSearchState
 * @param {string|null} p.spaceType group key, null = all
 * @param {Array} p.groups resolved groups
 * @param {string} [p.search] location.search of the current page (to keep foreign params)
 * @param {string} [p.pathname] location.pathname (to keep /s/:listingType)
 * @returns {string|null} path + query, or null when the page has no chips
 */
export const spaceTypeHref = ({ current = {}, spaceType, groups = [], search = '', pathname }) => {
  if (current.page === PAGE_CITY && current.where?.citySlug) {
    // Sorting and "verified only" stay when the type changes.
    return cityPath(current.where.citySlug, spaceType, current.filters);
  }
  if (current.page === PAGE_SEARCH) {
    const params = rawParams(search);
    const next = {};
    params.forEach((value, key) => {
      if (key !== CATEGORY_PARAM && key !== 'page' && !UI_ONLY_PARAMS.includes(key)) {
        next[key] = value;
      }
    });
    const group = groups.find(g => g.key === spaceType);
    if (group) {
      next[CATEGORY_PARAM] = group.ids.join(',');
    }
    const query = stringify(next);
    const path = pathname || SEARCH_PATH;
    return query ? `${path}?${query}` : path;
  }
  return null;
};

// ================ Counting ================ //

/**
 * The price param in the shape the Marketplace API wants, in subunits. The UI writes `min,max` in
 * CHF; a max at the filter's configured maximum means "no upper limit" and is sent
 * open-ended — the hosted maximum is CHF 500 and 32 of 129 spaces cost more (measured 2026-10-18),
 * so a closed range silently dropped every monthly office the moment the slider was touched.
 * `price=20000,` was verified against the live API (32 results in the Zürich box).
 *
 * @param {string} priceParam e.g. "0,500"
 * @param {Object} config app config (currency, search.defaultFilters)
 * @param {Object} [options]
 * @param {boolean} [options.openEnded] whether a max at the configured maximum is open-ended
 * @returns {string|null} e.g. "0,50001" or "0,"
 */
export const apiPriceParam = (priceParam, config, { openEnded = true } = {}) => {
  const values = `${priceParam ?? ''}`.split(',');
  if (!priceParam || values.length !== 2) {
    return null;
  }
  const divisor = unitDivisor(config?.currency || 'CHF');
  const toSub = value => convertUnitToSubUnit(Number(value), divisor);
  const min = Number(values[0]);
  const max = Number(values[1]);
  if (Number.isNaN(min) || Number.isNaN(max)) {
    return null;
  }
  const priceFilter = (config?.search?.defaultFilters || []).find(f => f.key === 'price');
  const configMax = priceFilter?.max;
  // Exactly the slider's maximum: the slider cannot go higher, so its end means "and more". A larger
  // max (the concierge's "unter 2000 Franken", a deep link) is a real limit and stays closed.
  if (openEnded && typeof configMax === 'number' && max === configMax) {
    return `${toSub(min)},`;
  }
  return `${toSub(min)},${toSub(max) + 1}`;
};

const COUNT_BASE = { page: 1, perPage: 1, minStock: 1, stockMode: 'match-undefined' };

/**
 * SDK params that count what a target URL will show (`perPage: 1`, read `meta.totalItems`).
 *
 * City page: its box, the three groups only (the page shows no lounges and no "others"), the
 *   chosen group when there is one.
 * /s: bounds, category, keywords, listing type (path or param), price, features.
 *
 * Returns null when the count cannot be exact — a dates or seats filter goes through availability
 * logic that lives in SearchPage.duck; the caller then shows no number rather than a wrong one.
 *
 * @param {string} url path + query, as built by buildSearchTarget / spaceTypeHref
 * @param {Object} p
 * @param {Array} p.groups resolved groups
 * @param {Object} p.config app config
 * @returns {Object|null}
 */
export const countParamsForUrl = (url, { groups = [], config } = {}) => {
  const [pathname, query = ''] = `${url || ''}`.split('?');
  const params = rawParams(query);
  const allGroupIds = groups.flatMap(g => g.ids);

  const cityMatch = pathname.match(/^\/coworking\/([^/]+)(?:\/([^/]+))?$/);
  if (cityMatch) {
    const slug = decodeURIComponent(cityMatch[1]);
    const city = getCityLandingConfig(slug);
    const bounds = city ? getBoundsForSdk(city.boundsStr) : null;
    if (!bounds) {
      return null;
    }
    const spaceType = cityMatch[2]
      ? cityTypeSlugToGroup(cityMatch[2])
      : groupForTypParam(params.get(CITY_TYPE_PARAM));
    const group = groups.find(g => g.key === spaceType);
    const ids = group ? group.ids : allGroupIds;
    return {
      ...COUNT_BASE,
      bounds,
      ...(ids.length > 0 ? { [CATEGORY_PARAM]: ids.join(',') } : {}),
    };
  }

  const searchMatch = pathname.match(/^\/s(?:\/([^/]+))?$/);
  if (!searchMatch) {
    return null;
  }
  if (params.get('dates') || params.get('seats')) {
    return null;
  }
  const out = { ...COUNT_BASE };
  const boundsStr = params.get('bounds');
  const bounds = boundsStr ? getBoundsForSdk(boundsStr) : null;
  if (bounds) {
    out.bounds = bounds;
  }
  const category = params.get(CATEGORY_PARAM);
  if (category) {
    out[CATEGORY_PARAM] = category;
  }
  const listingType = searchMatch[1]
    ? decodeURIComponent(searchMatch[1])
    : params.get('pub_listingType');
  if (listingType) {
    out.pub_listingType = listingType;
  }
  const keywords = params.get('keywords');
  if (keywords) {
    out.keywords = keywords;
  }
  const price = apiPriceParam(params.get('price'), config);
  if (price) {
    out.price = price;
  }
  params.forEach((value, key) => {
    if (key.startsWith('pub_') && key !== CATEGORY_PARAM && key !== 'pub_listingType') {
      out[key] = value;
    }
  });
  return out;
};

// ================ Geography ================ //

const toRad = deg => (deg * Math.PI) / 180;
const distanceKm = (a, b) => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

/** Centre of a bounds string `ne_lat,ne_lng,sw_lat,sw_lng`. */
export const boundsCenter = boundsStr => {
  const parts = `${boundsStr || ''}`.split(',').map(Number);
  if (parts.length !== 4 || parts.some(Number.isNaN)) {
    return null;
  }
  const [neLat, neLng, swLat, swLng] = parts;
  return { lat: (neLat + swLat) / 2, lng: (neLng + swLng) / 2 };
};

/**
 * Our cities ordered by distance from a point — for "the nearest city with spaces" in the
 * zero-result ladder. The caller filters by stock (it holds the counts).
 *
 * @param {{lat: number, lng: number}} point
 * @param {Object} [options]
 * @param {string[]} [options.exclude] slugs to leave out (the city itself)
 * @returns {Array<{ slug: string, km: number }>}
 */
export const citiesByDistance = (point, { exclude = [] } = {}) => {
  if (!point || typeof point.lat !== 'number' || typeof point.lng !== 'number') {
    return [];
  }
  return CITY_LANDING_SLUGS.filter(slug => !exclude.includes(slug))
    .map(slug => {
      const city = getCityLandingConfig(slug);
      const center = city?.center || boundsCenter(city?.boundsStr);
      return center ? { slug, km: Math.round(distanceKm(point, center)) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.km - b.km);
};
